import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import * as AmazonCognitoIdentity from 'amazon-cognito-identity-js';
import { Input } from 'common/components';
import { useCognito } from 'common/hooks';
import styles from '../ForgotPasswordModal.module.scss';

export type VerifyFormData = {
    verificationCode: string;
    newPassword: string;
    confirmPassword: string;
};

type VerifyStepProps = {
    form: UseFormReturn<VerifyFormData>;
    userEmail: string;
    cognitoUser: AmazonCognitoIdentity.CognitoUser;
    onSuccess: (email: string, password: string) => void;
    onBackToRequest: () => void;
};

export const VerifyStep: React.FC<VerifyStepProps> = ({ form, userEmail, cognitoUser, onSuccess, onBackToRequest }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [generalError, setGeneralError] = useState('');
    const { confirmPassword: confirmPasswordReset } = useCognito();

    const handleConfirmReset = async (data: VerifyFormData) => {
        // Clear any previous general errors
        setGeneralError('');
        setIsLoading(true);

        try {
            confirmPasswordReset(cognitoUser, data.verificationCode, data.newPassword, {
                onSuccess: () => {
                    onSuccess(userEmail, data.newPassword);
                    setIsLoading(false);
                },
                onFailure: (err) => {
                    console.error('Password reset confirmation failed:', err);
                    form.setError('verificationCode', { message: err.message || 'Failed to reset password' });
                    setIsLoading(false);
                },
            });
        } catch (err: any) {
            console.error('Password reset confirmation error:', err);
            setGeneralError(err.message || 'An unexpected error occurred');
            setIsLoading(false);
        }
    };
    return (
        <form id='verify-form' onSubmit={form.handleSubmit(handleConfirmReset)} noValidate>
            <div className={styles.stepContainer}>
                <div className={styles.description}>
                    We've sent a verification code to <strong>{userEmail}</strong>. Enter the code and your new password
                    below.
                </div>

                <Input
                    label='Verification Code'
                    type='text'
                    placeholder='Enter verification code'
                    disabled={isLoading}
                    error={!!form.formState.errors.verificationCode}
                    errorText={form.formState.errors.verificationCode?.message}
                    {...form.register('verificationCode', {
                        required: 'Please enter the verification code',
                        minLength: {
                            value: 6,
                            message: 'Verification code must be at least 6 characters',
                        },
                    })}
                />

                <Input
                    label='New Password'
                    type='password'
                    placeholder='Enter new password'
                    disabled={isLoading}
                    helperText='Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, one number, and one special character (e.g., !@#$%^&*)'
                    error={!!form.formState.errors.newPassword}
                    errorText={form.formState.errors.newPassword?.message}
                    {...form.register('newPassword', {
                        required: 'Please enter a new password',
                        minLength: {
                            value: 8,
                            message: 'Password must be at least 8 characters long',
                        },
                        pattern: {
                            value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/,
                            message:
                                'Password must contain at least one uppercase letter, one lowercase letter, and one number and one special character (e.g., !@#$%^&*)',
                        },
                    })}
                />

                <Input
                    label='Confirm New Password'
                    type='password'
                    placeholder='Confirm new password'
                    disabled={isLoading}
                    error={!!form.formState.errors.confirmPassword}
                    errorText={form.formState.errors.confirmPassword?.message}
                    {...form.register('confirmPassword', {
                        required: 'Please confirm your password',
                        validate: (value) => {
                            const newPassword = form.getValues('newPassword');
                            return value === newPassword || 'Passwords do not match';
                        },
                    })}
                />

                {generalError && <div className={styles.error}>{generalError}</div>}

                <button type='button' onClick={onBackToRequest} className={styles.backButton} disabled={isLoading}>
                    ← Back to email entry
                </button>
            </div>
        </form>
    );
};
